/**
 * MidPointHandle - Handle visual para dividir edges en mid-point
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/components
 *
 * @description
 * Renderiza un handle interactivo en el mid-point de un edge.
 * El usuario puede hacer click para dividir el edge.
 *
 * @example
 * ```tsx
 * <MidPointHandle
 *   screenCoordinates={[320, 240]}
 *   isNear={true}
 *   onClick={() => handleSplit()}
 * />
 * ```
 */

import { CSSProperties } from 'react';
import './mid-point-handle.css';

export interface MidPointHandleProps {
  /** Coordenadas de pantalla [x, y] donde renderizar */
  screenCoordinates: [number, number];
  /** Si el cursor está cerca del handle */
  isNear: boolean;
  /** Callback al hacer click */
  onClick: () => void;
}

/**
 * Handle visual para mid-point de edge
 */
export function MidPointHandle({
  screenCoordinates,
  isNear,
  onClick,
}: MidPointHandleProps): JSX.Element {
  const [x, y] = screenCoordinates;

  const style: CSSProperties = {
    position: 'absolute',
    left: x,
    top: y,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'auto',
  };

  return (
    <div
      className={`mid-point-handle ${isNear ? 'mid-point-handle--near' : ''}`}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title="Click para dividir segmento"
    >
      <div className="mid-point-handle__circle" />
      <div className="mid-point-handle__pulse" />
    </div>
  );
}
